import { useEffect, useRef, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { ChevronDown } from 'lucide-react'
import { markFreshNav } from '@/components/shell/trail-store'
import { cn } from '@/lib/utils'
import type { ErpView } from '../frentes'
import { splitTabs } from './calm-logic'

/* ERP · abas da experiência: as telas da experiência no menu da frente, no topo do conteúdo (a atual marcada). Uma
   linha só: as 6 primeiras à vista (a atual sempre) e o resto em "Mais (n) ▾", um menu como o do FrenteSwitcher (setas,
   Esc e clique fora fecham). Links de verdade; no celular não há "Mais": todas as abas ficam na linha, que rola na
   horizontal. */

const TABS_VISIBLE = 6

export function AreaTabs({ area, tabs, current }: { area: string; tabs: readonly ErpView[]; current: ErpView }) {
    const [open, setOpen] = useState(false)
    const ref = useRef<HTMLLIElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)
    const { shown, rest } = splitTabs(tabs, current.key, TABS_VISIBLE)

    useEffect(() => {
        if (!open) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return
            setOpen(false)
            ref.current?.querySelector<HTMLButtonElement>('button')?.focus()
        }
        const onDown = (e: PointerEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
        document.addEventListener('keydown', onKey)
        document.addEventListener('pointerdown', onDown)
        menuRef.current?.querySelector<HTMLAnchorElement>('[role="menuitem"]')?.focus()
        return () => {
            document.removeEventListener('keydown', onKey)
            document.removeEventListener('pointerdown', onDown)
        }
    }, [open])

    // Trocar de tela fecha o menu
    useEffect(() => setOpen(false), [current.key])

    if (tabs.length < 2) return null

    const onMenuKey = (e: React.KeyboardEvent) => {
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
        e.preventDefault()
        const items = [...(menuRef.current?.querySelectorAll<HTMLAnchorElement>('[role="menuitem"]') ?? [])]
        const i = items.indexOf(document.activeElement as HTMLAnchorElement)
        items[(i + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length]?.focus()
    }

    const tabClass = '-mb-px block border-b-2 px-3.5 py-2.5 text-[13.5px] whitespace-nowrap no-underline hover:text-fg'
    return (
        <nav aria-label={`Telas de ${area}`} className="-mb-1 overflow-x-auto sm:overflow-visible">
            <ul className="m-0 flex list-none flex-nowrap gap-x-0.5 border-b border-line p-0">
                {[...shown, ...rest].map((t, i) => {
                    const on = t.key === current.key
                    return (
                        <li key={t.key} className={cn('flex-none', i >= shown.length && 'sm:hidden')}>
                            <Link
                                to={t.path as '/'}
                                onClick={markFreshNav}
                                aria-current={on ? 'page' : undefined}
                                className={tabClass}
                                style={{ borderBottomColor: on ? 'var(--green)' : 'transparent', color: on ? 'var(--text)' : 'var(--muted)', fontWeight: on ? 600 : 500 }}
                            >
                                {t.label}
                            </Link>
                        </li>
                    )
                })}
                {rest.length > 0 && (
                    <li ref={ref} className="relative hidden flex-none sm:block">
                        <button
                            type="button"
                            aria-haspopup="menu"
                            aria-expanded={open}
                            onClick={() => setOpen(v => !v)}
                            className={cn(tabClass, 'flex cursor-pointer items-center gap-1 border-x-0 border-t-0 bg-transparent font-medium text-muted')}
                            style={{ borderBottomColor: 'transparent' }}
                        >
                            Mais <span className="tabular text-[12px]">({rest.length})</span>
                            <ChevronDown size={14} aria-hidden="true" className="transition-transform motion-reduce:transition-none" style={{ transform: open ? 'rotate(180deg)' : undefined }} />
                        </button>
                        {open && (
                            <div
                                ref={menuRef}
                                role="menu"
                                aria-label={`Mais telas de ${area}`}
                                onKeyDown={onMenuKey}
                                className="absolute top-full right-0 z-40 mt-1 flex min-w-[220px] flex-col gap-px rounded-[12px] border border-line bg-panel p-1"
                                style={{ boxShadow: '0 12px 32px var(--shadow), 0 2px 6px var(--shadow)' }}
                            >
                                {rest.map(t => (
                                    <Link
                                        key={t.key}
                                        role="menuitem"
                                        to={t.path as '/'}
                                        onClick={() => { markFreshNav(); setOpen(false) }}
                                        className="flex min-h-8 w-full items-center rounded-[8px] px-2.5 text-[13px] text-fg no-underline hover:bg-[var(--hover)] focus-visible:bg-[var(--hover)]"
                                    >
                                        {t.label}
                                    </Link>
                                ))}
                            </div>
                        )}
                    </li>
                )}
            </ul>
        </nav>
    )
}
